import { groupCubeRows, usd, type ExecutiveTransaction } from "../../data/executiveOverview";

const SCORING_MODEL = [
  { label: "Operational",   weight: "25 %" },
  { label: "Supply Chain",  weight: "25 %" },
  { label: "Geopolitical",  weight: "20 %" },
  { label: "Reputational",  weight: "15 %" },
  { label: "ESG",           weight: "15 %" },
];

const THRESHOLDS = [
  { label: "Critical", color: "#ef4444", value: "> 4.0" },
  { label: "High",     color: "#f59e0b", value: "> 3.0" },
  { label: "Moderate", color: "#6b7280", value: "> 2.0" },
  { label: "Low",      color: "#3b82f6", value: "< 2.0" },
];

const GEO_RISK: Record<string, number> = {
  APAC: 3.0,
  "Latin America": 3.6,
  EMEA: 2.4,
  "North America": 1.8,
};

function riskBand(score: number) {
  if (score > 4) return "Critical";
  if (score > 3) return "High";
  if (score > 2) return "Moderate";
  return "Low";
}

const SMOOTHING = 5;

function scoreSupplier(supplierRows: ExecutiveTransaction[], globalRates: { op: number; sc: number; rep: number; esg: number }) {
  const n = supplierRows.length;
  const clamp = (value: number) => Math.min(5, Math.max(1, value));
  // Blend each supplier's own rate with the portfolio-wide rate so a supplier with only
  // one or two transactions doesn't snap straight to a score of 1 or 5.
  const smoothedShare = (pred: (row: ExecutiveTransaction) => boolean, globalRate: number) =>
    (supplierRows.filter(pred).length + SMOOTHING * globalRate) / (n + SMOOTHING);

  const op = clamp(1 + 4 * smoothedShare((r) => r.Contract_Status !== "Contracted", globalRates.op));
  const sc = clamp(1 + 4 * smoothedShare((r) => r.Maverick_Spend_Flag === "Yes" || r.Tail_Spend_Flag === "Yes", globalRates.sc));
  const rep = clamp(1 + 4 * smoothedShare((r) => r.Duplicate_Flag === "Yes" || r.Data_Quality_Status === "Potential Duplicate", globalRates.rep));
  const esg = clamp(1 + 4 * smoothedShare((r) => r.Classification_Status === "Review Required" || r.Data_Quality_Status === "Missing PO", globalRates.esg));
  const spend = supplierRows.reduce((sum, r) => sum + r.Spend_Amount_USD, 0);
  const geo = clamp(supplierRows.reduce((sum, r) => sum + (GEO_RISK[r.Geography] || 3), 0) / n);

  const final = op * 0.25 + sc * 0.25 + geo * 0.2 + rep * 0.15 + esg * 0.15;
  const category = groupCubeRows(supplierRows, "Category_L2")[0]?.label || "Unclassified";

  return { supplier: supplierRows[0].Parent_Supplier, category, spend, op, sc, rep, esg, geo, final, risk: riskBand(final) };
}

const ACTION_TEXT: Record<string, (category: string) => string> = {
  Critical: (category) => `Run competitive RFx on ${category} immediately to benchmark price and resilience. Cap awarded share at 50%; develop a qualified secondary supplier.`,
  High: (category) => `Run competitive RFx on ${category} within 6 months to benchmark price and resilience. Cap awarded share at 50%; develop a qualified secondary supplier.`,
  Moderate: () => "Monitor performance quarterly; no immediate sourcing action required.",
  Low: () => "No action required at this time.",
};

function RadarChart({ scores }: { scores: number[] }) {
  const cx = 50, cy = 55, r = 42;
  const angles = [-90, -18, 54, 126, 198];
  const pts = angles.map((a) => {
    const rad = (a * Math.PI) / 180;
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
  });
  const innerPts = pts.map((p, i) => {
    const scale = Math.max(0, Math.min(1, scores[i] / 5));
    return { x: cx + (p.x - cx) * scale, y: cy + (p.y - cy) * scale };
  });

  return (
    <svg viewBox="0 0 100 110" style={{ width: "100%", height: "100%", maxHeight: 300 }}>
      {[1, 0.75, 0.5, 0.25].map((scale, i) => (
        <polygon
          key={i}
          points={pts.map((p) => `${cx + (p.x - cx) * scale},${cy + (p.y - cy) * scale}`).join(" ")}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth="0.5"
        />
      ))}
      {pts.map((p, i) => (
        <line key={i} x1={cx} y1={cy} x2={p.x} y2={p.y} stroke="#e5e7eb" strokeWidth="0.5" />
      ))}
      <polygon
        points={innerPts.map((p) => `${p.x},${p.y}`).join(" ")}
        fill="rgba(108,92,231,0.25)"
        stroke="#6c5ce7"
        strokeWidth="1"
      />
      {["Operational", "Supply Chain", "Reputational", "ESG", "Geopolitical"].map((label, i) => {
        const p = pts[i];
        const dx = p.x < cx ? -2 : p.x > cx ? 2 : 0;
        const dy = p.y < cy ? -2 : p.y > cy ? 2 : 0;
        return (
          <text
            key={i}
            x={p.x + dx}
            y={p.y + dy}
            fontSize="4.5"
            textAnchor={p.x < cx - 5 ? "end" : p.x > cx + 5 ? "start" : "middle"}
            dominantBaseline={p.y < cy ? "auto" : "hanging"}
            fill="#6b7280"
          >
            {label}
          </text>
        );
      })}
    </svg>
  );
}

export function SpendAiRiskImagePanel({ rows }: { rows: ExecutiveTransaction[] }) {
  const bySupplier = new Map<string, ExecutiveTransaction[]>();
  for (const row of rows) {
    const list = bySupplier.get(row.Parent_Supplier) || [];
    list.push(row);
    bySupplier.set(row.Parent_Supplier, list);
  }
  const n = rows.length || 1;
  const globalRates = {
    op: rows.filter((r) => r.Contract_Status !== "Contracted").length / n,
    sc: rows.filter((r) => r.Maverick_Spend_Flag === "Yes" || r.Tail_Spend_Flag === "Yes").length / n,
    rep: rows.filter((r) => r.Duplicate_Flag === "Yes" || r.Data_Quality_Status === "Potential Duplicate").length / n,
    esg: rows.filter((r) => r.Classification_Status === "Review Required" || r.Data_Quality_Status === "Missing PO").length / n,
  };
  const scored = Array.from(bySupplier.values(), (supplierRows) => scoreSupplier(supplierRows, globalRates))
    .sort((first, second) => second.final - first.final);

  const riskSummary = THRESHOLDS.map((t) => {
    const members = scored.filter((s) => s.risk === t.label);
    return {
      ...t,
      count: members.length,
      spend: usd(members.reduce((sum, s) => sum + s.spend, 0)),
    };
  });

  const avg = (key: "op" | "sc" | "rep" | "esg" | "geo") =>
    scored.length ? scored.reduce((sum, s) => sum + s[key], 0) / scored.length : 0;
  const radarScores = [avg("op"), avg("sc"), avg("rep"), avg("esg"), avg("geo")];

  const scorecard = scored.slice(0, 15);

  return (
    <div className="sairp-panel">
      {/* Header */}
      <div className="sairp-header-row">
        <div className="sairp-warning-icon">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18.108 14.9999L11.4414 3.33319C11.296 3.0767 11.0852 2.86335 10.8305 2.71492C10.5757 2.56649 10.2862 2.48828 9.99136 2.48828C9.69654 2.48828 9.40699 2.56649 9.15226 2.71492C8.89753 2.86335 8.68673 3.0767 8.54136 3.33319L1.8747 14.9999C1.72777 15.2543 1.65072 15.5431 1.65137 15.837C1.65202 16.1308 1.73035 16.4192 1.8784 16.673C2.02646 16.9269 2.23899 17.137 2.49444 17.2822C2.7499 17.4274 3.0392 17.5025 3.33303 17.4999H16.6664C16.9588 17.4996 17.246 17.4223 17.4991 17.2759C17.7522 17.1295 17.9624 16.9191 18.1085 16.6658C18.2545 16.4125 18.3314 16.1252 18.3313 15.8328C18.3312 15.5404 18.2542 15.2531 18.108 14.9999Z" stroke="white" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M10 7.5V10.8333" stroke="white" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M10 14.166H10.0083" stroke="white" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
        </div>
        <div className="sairp-header-title">Spend at Risk</div>
      </div>
      <div className="sairp-header-subtitle">Multi-parameter scoring: Operational · Supply Chain · Reputational · ESG · Geopolitical</div>

      {/* Risk summary cards */}
      <div className="sairp-risk-summary-grid">
        {riskSummary.map((r) => (
          <div key={r.label} className={`sairp-risk-card sairp-risk-card--${r.label.toLowerCase()}`}>
            <div className="sairp-risk-card__level">{r.label}</div>
            <div className="sairp-risk-card__count">{r.count}</div>
            <div className="sairp-risk-card__meta">suppliers · {r.spend}</div>
          </div>
        ))}
      </div>

      {/* Portfolio Risk Profile + Scoring Model */}
      <div className="sairp-top-grid">
        <div className="sairp-card">
          <div className="sairp-card__title">Portfolio Risk Profile (Avg Scores)</div>
          <div className="sairp-radar-wrap">
            <RadarChart scores={radarScores} />
          </div>
        </div>

        <div className="sairp-card">
          <div className="sairp-card__title">Scoring Model</div>
          {SCORING_MODEL.map((s) => (
            <div key={s.label} className="sairp-model-row">
              <span className="sairp-model-label">{s.label}</span>
              <span className="sairp-model-weight">weight {s.weight}</span>
            </div>
          ))}
          <div className="sairp-threshold-list">
            {THRESHOLDS.map((t) => (
              <div key={t.label} className="sairp-threshold-row">
                <span className={`sairp-threshold-label sairp-threshold-label--${t.label.toLowerCase()}`}>{t.label}</span>
                <span className="sairp-threshold-value">{t.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Supplier Risk Scorecard */}
      <div className="sairp-card">
        <div className="sairp-scorecard-title">Supplier Risk Scorecard</div>
        <div className="sairp-table-wrap">
          <table className="sairp-table">
            <thead>
              <tr className="sairp-thead-tr">
                {["Supplier", "Category", "Spend", "OP", "SC", "REP", "ESG", "GEO", "Final", "Risk", "Action Required"].map((h) => (
                  <th key={h} className="sairp-th">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {scorecard.map((row) => (
                <tr key={row.supplier} className="sairp-tbody-tr">
                  <td className="sairp-td sairp-td--supplier">{row.supplier}</td>
                  <td className="sairp-td sairp-td--muted">{row.category}</td>
                  <td className="sairp-td sairp-td--bold">{usd(row.spend)}</td>
                  {[row.op, row.sc, row.rep, row.esg, row.geo].map((v, j) => (
                    <td key={j} className="sairp-td sairp-td--center">
                      <span className="sairp-score-badge">{v.toFixed(1)}</span>
                    </td>
                  ))}
                  <td className="sairp-td sairp-td--center">
                    <span className="sairp-final-badge">{row.final.toFixed(2)}</span>
                  </td>
                  <td className="sairp-td">
                    <span className="sairp-risk-pill">{row.risk}</span>
                  </td>
                  <td className="sairp-td sairp-td--action">
                    {ACTION_TEXT[row.risk](row.category)}
                  </td>
                </tr>
              ))}
              {!scorecard.length && (
                <tr className="sairp-tbody-tr">
                  <td className="sairp-td" colSpan={11}>No spend matches the selected filters.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default SpendAiRiskImagePanel;
